import { useCallback, useEffect, useRef, useState } from 'react';
import Popover from '@mui/material/Popover';
import { useAppState, type AppSnapshot } from '../../app/state';
import { callAction, type WorkflowTransitionResponse } from '../../app/actions';
import { apiRequest } from '../../api/client';
import {
  PIPELINE_COLOR,
  PIPELINE_LABEL,
  WORKFLOW_VERBS,
  asWorkflowState,
} from '../../lib/docs';
import { canActOnWorkflow, canEditDoc, canEditPrompt } from '../../lib/responsibility';
import { capAi } from '../../lib/capabilities';
import type { DocId, NodeId, WorkflowState } from '../../lib/types';

/**
 * The doc bar's workflow control (V0.38, the successor of Phase 4a's ReviewControl) — one
 * page in one language, five states, four verbs. THE ONLY PLACE A TRANSITION HAPPENS in the
 * whole app; the pill dots in the node popup are read-only views of the same fact, and the
 * AI console's per-row state buttons were REMOVED with the unification, on purpose: a
 * transition belongs where the text being judged is readable, and a second way to approve a
 * page is a second workflow to keep in agreement.
 *
 * ── IT KEYS ON THE RESOLVED LOCALE, NOT `currentLocale`, AND THAT IS THE WHOLE DESIGN ────
 * `GET /api/docs/:id?lang=fr` falls back to the default locale when there is no French
 * translation, and reports the locale it actually RESOLVED — and since V0.38 the bulk
 * workflows map resolves the same fallback server-side, so the two sources cannot disagree.
 * The overlay shows that fallback silently — which is right, a reader should see the page
 * rather than a hole — so an editor with the language picker on French can be looking at
 * English text. Keyed on `currentLocale`, this control would then offer to approve a French
 * page that does not exist. So a FALLBACK entry renders READ-ONLY — state, history and
 * responsibles shown, verbs and picker withheld — with a hint naming the language you would
 * actually be acting on.
 *
 * ── WHERE THE STATE COMES FROM, AND WHY IT IS TWO SOURCES ────────────────────────────────
 * `S.docWorkflow` is the bulk map (`GET /api/docs/workflows`), fallback-resolved, and when
 * loaded it is the truth for state/locale/fallback/responsibles. The page's own
 * `GET /api/docs/:id` supplies what the bulk map deliberately omits — `reviewedByName` — and
 * stands in entirely while the bulk fetch has not landed. A content save updates the chip
 * WITHOUT A REFETCH: the translation PUT returns the row after the automatic demotion,
 * `DocOverlay` hands it to `noteDocWorkflow`, and the entry changes here on the next publish
 * — including the case where the save CREATES the missing translation and a `fallback: true`
 * entry flips to a real one.
 *
 * ── ONE `GET /api/docs/:id` PER OPEN, NEVER TWO ─────────────────────────────────────────
 * That page is where `reviewedByName` comes from, and in EDIT mode `DocOverlay` is already
 * fetching it for the editor. So it hands the answer down as `seed` and this control does not
 * ask again; it fetches for itself only in view mode, where nobody else does.
 * `e2e/34-doc-overlay.spec.ts` counts those requests and requires exactly one per open.
 *
 * The events trail is fetched lazily, when the panel opens: nobody reads a history they have
 * not asked for, and the doc bar is drawn on every document open.
 */

/** What `GET /api/docs/:id` tells us about the workflow. `locale` is the RESOLVED one. */
interface LoadedWorkflow {
  id: DocId;
  locale: string;
  workflowState: WorkflowState;
  reviewedAt: string | null;
  reviewedByName: string | null;
}

/** Normalise whatever `GET /api/docs/:id` returned into the fields this control needs. */
function readWorkflow(
  docId: DocId,
  fallbackLocale: string,
  d: { locale?: string; workflowState?: string; reviewedAt?: string | null; reviewedByName?: string | null },
): LoadedWorkflow {
  return {
    id: docId,
    locale: d.locale || fallbackLocale,
    workflowState: asWorkflowState(d.workflowState),
    reviewedAt: d.reviewedAt ?? null,
    reviewedByName: d.reviewedByName ?? null,
  };
}

interface WorkflowEvent {
  id: string;
  fromState: string;
  toState: string;
  trigger: 'manual' | 'content_change' | 'migration' | string;
  note: string | null;
  actorLabel: string;
  createdAt: string;
}

const dateOf = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

const timeOf = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** The node whose PRIMARY slot carries this doc, or null — what Regenerate is about. */
function primaryOwnerOf(s: AppSnapshot, docId: DocId): NodeId | null {
  for (const id in s.nodes) if (s.nodes[id].docId === docId) return id;
  return null;
}

export function WorkflowControl({
  docId,
  seed,
}: {
  docId: DocId;
  /**
   * The page as `DocOverlay` already loaded it, or `undefined` in view mode where nobody has.
   * `null` means "I am loading it, wait" — the difference between the two is what stops this
   * control racing the overlay to the same endpoint. See the header.
   */
  seed?: { id: string; locale?: string; workflowState?: string; reviewedAt?: string | null; reviewedByName?: string | null } | null;
}) {
  const s = useAppState();
  const seeded = seed !== undefined;
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<LoadedWorkflow | null>(null);
  const [events, setEvents] = useState<WorkflowEvent[] | null>(null);
  /** Which verb's optional note is being written, or null. `reject` and `flag` both ask. */
  const [noteFor, setNoteFor] = useState<'reject' | 'flag' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  /* Adopt the overlay's fetch when there is one. A transition replaces `loaded` afterwards,
     so this only re-runs when the seed itself changes — which is a fresh load of the page. */
  useEffect(() => {
    if (!seeded) return;
    setLoaded(seed && seed.id === docId ? readWorkflow(docId, s.currentLocale, seed) : null);
    setEvents(null);
    setNoteFor(null);
    setNote('');
  }, [seeded, seed, docId, s.currentLocale]);

  /* View mode: nobody else is asking, so ask. `docOpenSeq` re-asks when the same page is
     opened again — a revision restore does exactly that and can change the state. */
  useEffect(() => {
    if (seeded) return;
    let live = true;
    setLoaded(null);
    setEvents(null);
    setNoteFor(null);
    setNote('');
    apiRequest<Partial<LoadedWorkflow>>(`/api/docs/${docId}`, { query: { lang: s.currentLocale } }).then(
      (d) => {
        if (live) setLoaded(readWorkflow(docId, s.currentLocale, d));
      },
      () => {
        // A server that predates the workflow, or a page that has gone: the chip stays hidden
        // rather than claiming a state nobody reported.
        if (live) setLoaded(null);
      },
    );
    return () => {
      live = false;
    };
  }, [seeded, docId, s.currentLocale, s.docOpenSeq]);

  // The bulk entry — fallback-resolved server-side, the truth once loaded. See the header.
  const entry = s.docWorkflowLoaded ? s.docWorkflow[docId] : undefined;
  const locale = entry ? entry.locale : loaded?.locale;
  const state: WorkflowState | null = entry ? entry.workflowState : (loaded?.workflowState ?? null);
  const reviewedAt = entry ? entry.reviewedAt : (loaded?.reviewedAt ?? null);
  // Only meaningful while it belongs to the stamp on screen: a demotion clears `reviewedAt`,
  // so a name with no date beside it is never rendered.
  const reviewedBy = loaded?.reviewedByName ?? null;

  const loadEvents = useCallback(() => {
    if (!locale) return;
    setEvents(null);
    apiRequest<{ events: WorkflowEvent[] }>(`/api/docs/${docId}/review/events`, { query: { locale } }).then(
      (r) => setEvents(r.events || []),
      () => setEvents([]),
    );
  }, [docId, locale]);

  const move = async (verb: 'request' | 'approve' | 'reject' | 'flag') => {
    if (!locale || busy) return;
    setBusy(true);
    const done = (await callAction(
      'setDocWorkflow',
      docId,
      locale,
      verb,
      verb === 'reject' || verb === 'flag' ? note : undefined,
    )) as WorkflowTransitionResponse | null;
    setBusy(false);
    setNoteFor(null);
    setNote('');
    if (done) {
      // The response is authoritative for this page+locale; kept locally too so the chip is
      // right even while the bulk map is between fetches.
      setLoaded({
        id: docId,
        locale: done.locale,
        workflowState: done.workflowState,
        reviewedAt: done.reviewedAt,
        reviewedByName: done.reviewedByName,
      });
      loadEvents();
    }
  };

  // Nothing to show until the page has answered: a chip that appears as "Draft" and corrects
  // itself a beat later is worse than one that arrives once.
  if (!state || !locale) return null;

  const fallback = entry ? entry.fallback : locale !== s.currentLocale;
  /**
   * The VERBS are scoped (`mayActOnWorkflow`, server-side): the page's own responsibles for
   * this locale, an effective responsible of an owning node, EDIT_ALL_DOCS — and NEVER on a
   * fallback: an editor reading an English stand-in must not act on a French page that does
   * not exist (the V0.36 trap, closed structurally now that the entry says `fallback`). The
   * chip and its history stay readable for every participant — knowing where a page stands
   * is not the same act as moving it.
   */
  const mayAct = !fallback && canActOnWorkflow(s, docId, locale);
  const offered = WORKFLOW_VERBS.filter((v) => v.to !== state);

  /**
   * Translate (V0.41): a FALLBACK page is the one place this appears — the moment somebody is
   * reading the source because their own language does not exist yet is the moment to offer
   * writing it. Gated on the edit scope of the language they would be CREATING (`currentLocale`,
   * not the resolved fallback `locale`) and on the deployment having the `translate` capability.
   * It is the single action a fallback page can offer, its workflow verbs being withheld.
   */
  const mayTranslate = fallback && capAi(s.caps, 'translate') && canEditDoc(s, docId, s.currentLocale);

  /* Regenerate (V0.38): only for the node whose PRIMARY doc this is — generation writes that
     slot and no other — for the prompt scope, where the deployment can generate at all. */
  const ownerId = primaryOwnerOf(s, docId);
  const mayRegenerate = !!ownerId && capAi(s.caps, 'docgen') && canEditPrompt(s, ownerId);

  return (
    <>
      <button
        type="button"
        id="doc-workflow"
        className={`workflowchip ${state}`}
        ref={btnRef}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        title={`Workflow: ${PIPELINE_LABEL[state]}${fallback ? ` (${locale} version)` : ''}`}
        onClick={() => {
          setOpen((o) => {
            if (!o) loadEvents();
            return !o;
          });
        }}
      >
        <span className="reviewdot" style={{ background: PIPELINE_COLOR[state] }} />
        {PIPELINE_LABEL[state]}
      </button>

      <Popover
        open={open}
        anchorEl={btnRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className="workflowpanel">
          <div className="review-head">
            <span className="review-title">Documentation workflow</span>
            <span className="review-lang">{locale}</span>
          </div>

          {fallback ? (
            <div className="review-fallback">
              There is no <b>{s.currentLocale}</b> version of this page, so you are reading the{' '}
              <b>{locale}</b> one. Its workflow is shown read-only — switch the language to{' '}
              <b>{locale}</b> to act on it.
            </div>
          ) : null}

          {/* Translate into the reader's own language — the one action a fallback page offers,
              to whoever may write that language and where the deployment can translate. */}
          {mayTranslate ? (
            <div className="review-actions" style={{ marginTop: 4 }}>
              <button
                className="pbtn primary"
                id="wf-translate"
                disabled={translating}
                title={`Create the ${s.currentLocale.toUpperCase()} version by translating the ${locale.toUpperCase()} one with the AI.`}
                onClick={() => {
                  setTranslating(true);
                  void (callAction('translateDoc', docId, s.currentLocale) as Promise<boolean>).finally(
                    () => setTranslating(false),
                  );
                }}
              >
                {translating ? 'Translating…' : `Translate into ${s.currentLocale.toUpperCase()}`}
              </button>
            </div>
          ) : null}

          {/* WHO LAST MOVED THIS STATE, and when — never "approved by". The pair
              (reviewedByName, reviewedAt) records the last transition's actor, which is not the
              same claim as an approval; the state chip above already says WHAT that state is. */}
          <div className="review-meta">
            {reviewedAt
              ? `Last changed${reviewedBy ? ` by ${reviewedBy}` : ''} on ${dateOf(reviewedAt)}`
              : 'No changes recorded yet.'}
          </div>

          {/* The responsibles moved OUT of this popover into the doc bar's ownership cluster
              (V0.42, DocOwnershipCluster) — one implementation. The chip here is state, history
              and verbs only. */}

          {mayAct ? (
            <div className="review-actions">
              {offered.map((v) =>
                (v.verb === 'reject' || v.verb === 'flag') && noteFor !== v.verb ? (
                  <button
                    key={v.verb}
                    className="pbtn"
                    disabled={busy}
                    onClick={() => {
                      setNoteFor(v.verb as 'reject' | 'flag');
                      setNote('');
                    }}
                  >
                    {v.action}
                  </button>
                ) : v.verb === 'reject' || v.verb === 'flag' ? null : (
                  <button
                    key={v.verb}
                    className={v.verb === 'approve' ? 'pbtn primary' : 'pbtn'}
                    disabled={busy}
                    onClick={() => void move(v.verb)}
                  >
                    {v.action}
                  </button>
                ),
              )}
            </div>
          ) : null}

          {noteFor && mayAct ? (
            <div className="review-reject">
              <textarea
                className="review-note"
                placeholder={noteFor === 'reject' ? 'What needs changing? (optional)' : 'What is outdated? (optional)'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="review-actions">
                <button
                  className="pbtn"
                  onClick={() => {
                    setNoteFor(null);
                    setNote('');
                  }}
                >
                  Cancel
                </button>
                <button className="pbtn primary" disabled={busy} onClick={() => void move(noteFor)}>
                  {WORKFLOW_VERBS.find((v) => v.verb === noteFor)!.action}
                </button>
              </div>
            </div>
          ) : null}

          {mayRegenerate ? (
            <div className="review-actions" style={{ marginTop: 9 }}>
              <button
                className="pbtn"
                id="wf-regenerate"
                disabled={regenerating}
                title="Rewrite this page with the AI. Human edits since the last generation are guarded — you will be asked before they are overwritten."
                onClick={() => {
                  setRegenerating(true);
                  void (callAction('generateDocForNode', ownerId) as Promise<boolean>).finally(() =>
                    setRegenerating(false),
                  );
                }}
              >
                {regenerating ? 'Regenerating…' : 'Regenerate'}
              </button>
            </div>
          ) : null}

          <div className="review-sect">Recent activity</div>
          {events === null ? (
            <div className="review-empty">Loading…</div>
          ) : !events.length ? (
            <div className="review-empty">Nothing yet.</div>
          ) : (
            <ul className="review-events">
              {events.slice(0, 6).map((e) => (
                <li key={e.id}>
                  <span
                    className="reviewdot"
                    style={{ background: PIPELINE_COLOR[asWorkflowState(e.toState)] }}
                  />
                  <span className="review-ev-text">
                    {/* A mechanical transition has an actor but no decision: saying "X
                        approved" and "the page was edited, so this is a draft again" in the
                        same voice is what makes the trail readable. A content_change landing
                        on 'generated' is the AI's rewrite, and says so. */}
                    {e.trigger === 'content_change'
                      ? asWorkflowState(e.toState) === 'generated'
                        ? `${PIPELINE_LABEL.generated} — the AI rewrote the page`
                        : `${PIPELINE_LABEL[asWorkflowState(e.toState)]} — the page was edited`
                      : `${PIPELINE_LABEL[asWorkflowState(e.toState)]} — ${e.actorLabel}`}
                    {e.note ? <span className="review-ev-note">“{e.note}”</span> : null}
                  </span>
                  <span className="review-ev-when">{timeOf(e.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Popover>
    </>
  );
}
